import type { ArtifactKind, EvArtifact } from "../types/agent-chat";

/** File-name prefix per artifact kind; mirrors api/app/agent/export/docx.py. */
const KIND_SLUGS: Record<string, string> = {
  description_poste: "description-de-poste",
  echelle_salariale: "echelle-salariale",
  document_modele: "document",
};

/** ASCII, lowercase, dash-separated form of a title, capped in length. */
export function slugify(text: string, maxLength = 60): string {
  const folded = (text ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  let slug = folded.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (slug.length > maxLength) slug = slug.slice(0, maxLength).replace(/-+$/g, "");
  return slug;
}

/** The file name the server would pick, used when its header is unreadable. */
export function artifactFilename(
  artifact: Pick<EvArtifact, "kind" | "fields">,
  today: Date = new Date(),
  extension = "docx"
): string {
  const kind = artifact.kind as ArtifactKind | string;
  const prefix = KIND_SLUGS[kind] ?? slugify(kind) ?? "document";
  const fields = artifact.fields ?? {};
  const subjectRaw = [fields.titre, fields.poste].find((v) => typeof v === "string" && slugify(v));
  const subject = typeof subjectRaw === "string" ? slugify(subjectRaw, 40) : "";
  const stamp = today.toISOString().slice(0, 10);
  return [prefix, subject, stamp].filter(Boolean).join("-") + `.${extension}`;
}

/** The file name carried by a Content-Disposition header, if any. */
export function filenameFromDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const extended = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ""));
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/.exec(header);
  return plain ? plain[1].trim() : null;
}

/** Trigger a browser download of a Blob under the given name. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => {
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, 0);
}
